import { makeAutoObservable, runInAction } from 'mobx'
import { isOptOutCurrentInbox } from 'entities/Contacts'
import { Inbox } from 'entities/Inbox/model/Inbox'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import type { ConversationMessagesStore } from 'widgets/ConversationMessages'

export class ConversationMessageFieldAccessStore {
  private _isHideInboxAlert = false
  private _loading = true

  constructor(
    private _conversationMessagesStore: ConversationMessagesStore,
    private _entities: IEntitiesStore
  ) {
    makeAutoObservable(this)
  }

  get loading() {
    return this._loading
  }

  get isHideInboxAlert() {
    return this._isHideInboxAlert
  }

  get isOnboardingFlow() {
    if (this._entities.organizationStore.isTwilioNumberVendor) return false

    const inbox = this.inbox

    if (inbox?.type !== 'inbox') return false

    const number = this.conversationInboxNumber

    return Boolean(
      inbox.isMainIdentifier ||
        inbox.isTrialIdentifier ||
        (!this._entities.subscriptionStore.isTrial &&
          !number?.is_aircall &&
          ((number?.isTollFree && !number?.isStatusVerified) ||
            (number?.isLocal && !number?.isStatusVerified)))
    )
  }

  get contact() {
    return this._conversationMessagesStore.contact
  }

  get conversation() {
    return this._conversationMessagesStore.conversation
  }

  get inbox() {
    if (this._conversationMessagesStore.sendFromStore) {
      const sendFromInbox = this._conversationMessagesStore.sendFromStore.inbox
      if (sendFromInbox instanceof Inbox) {
        return sendFromInbox
      }
    }
    if (this._conversationMessagesStore.inbox?.type !== 'inbox') return

    return this._conversationMessagesStore.inbox
  }

  get conversationInboxNumber() {
    if (this._conversationMessagesStore.sendFromStore) {
      return this._conversationMessagesStore.sendFromStore.number
    }
    const inbox = this.inbox
    const numberFromCurrentConversation = this._entities.numbersStore.getItem(
      this.conversation?.number_id
    )
    const numberFromInbox = this._entities.numbersStore.getItem(inbox?.numberId)

    return numberFromCurrentConversation || numberFromInbox
  }

  get isMessageLocal() {
    const inbox = this.inbox

    if (inbox?.type !== 'inbox') return false

    const number = this._entities.numbersStore.getItem(inbox?.numberId)
    const contact = this.contact

    if (contact?.isOptOutHard) return false
    if (number?.isShortCode) return false

    return this.isOptOut || this.isOptOutCurrentInbox
  }

  get isMessageAll() {
    const inbox = this.inbox

    if (inbox?.type !== 'inbox') return false

    const number = this._entities.numbersStore.getItem(inbox?.numberId)
    const contact = this.contact

    if (!contact) return false
    if (number?.isShortCode) return this.isOptOutCurrentInbox
    if (contact?.isOptOutHard) return true

    return Boolean(
      contact?.optItems.length === contact?.optItems.filter((item) => !item.value).length
    )
  }

  get isOptOut() {
    if (!this.contact) return false

    return Boolean(this.contact?.isOptOut)
  }

  get isOptOutCurrentInbox() {
    if (!this.contact) return false

    return isOptOutCurrentInbox({
      contact: this.contact,
      conversation: this.conversation,
      entities: this._entities,
    })
  }

  get isStatusPending() {
    return Boolean(
      this.conversationInboxNumber?.isTollFree &&
        !this.conversationInboxNumber?.is_aircall &&
        (this.conversationInboxNumber?.isStatusUnderVerification ||
          this.conversationInboxNumber?.isStatusInternalReview)
    )
  }

  get isStatusBlockedContact() {
    if (this.contact?.is_blocked) return true

    const contacts = this._conversationMessagesStore.contacts
    return Boolean(contacts?.some((contact) => contact?.is_blocked))
  }

  setHideInboxAlert = () => {
    this._isHideInboxAlert = true
  }

  init = async () => {
    runInAction(() => {
      this._loading = true
    })

    await Promise.all([
      this._entities.inboxesStore.fetchInboxes(),
      this._entities.usersStore.initPromise,
      this._entities.subscriptionStore.initPromise,
      this._entities.organizationStore.initPromise,
    ])

    runInAction(() => {
      this._loading = false
    })
  }
}
